import { IconButton, InputAdornment, useTheme } from "@mui/material";
import { CloseIcon, SearchIcon } from "components/icons";
import type React from "react";
import { useEffect, useState } from "react";
import { Input, type InputProps } from "./Input";

interface SearchInputProps extends InputProps {
  /**
   * Opt-in clear button. When provided, a × shows while the field has text;
   * clicking it empties the field immediately and fires this callback (the
   * parent should reset its own value here). Omitted → no button, so the 100+
   * existing usages are unaffected.
   */
  onClear?: () => void;
}

export const SearchInput = ({
  sx,
  onChange,
  value,
  onClear,
  ...rest
}: SearchInputProps) => {
  const theme = useTheme();
  const [timerId, setTimerId] = useState<ReturnType<typeof setTimeout>>();

  const [inputValue, setInputValue] = useState(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    const id = setTimeout(() => {
      clearTimeout(timerId);
      onChange?.(e);
    }, 350);
    setTimerId(id);
    e.stopPropagation();
  };

  const handleClear = () => {
    if (timerId) clearTimeout(timerId);
    setInputValue("");
    onClear?.();
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const showClear = Boolean(onClear) && String(inputValue ?? "").length > 0;

  return (
    <Input
      hideHelperBox
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" width={"14px"} height={"14px"} />
          </InputAdornment>
        ),
        endAdornment: showClear ? (
          <InputAdornment position="end">
            <IconButton
              aria-label="Clear search"
              size="small"
              edge="end"
              onClick={handleClear}
              sx={{ color: theme.palette.lightBg.low }}
            >
              <CloseIcon sx={{ fontSize: "24px" }} />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
      sx={{
        "& .MuiInputBase-input": {
          paddingLeft: 0,
        },
        "& .MuiOutlinedInput-root": {
          // bgcolor: "red",
          bgcolor: theme.palette.neutral["005"],

          "& > fieldset": {
            border: `1px solid ${theme.palette.neutral["010"]}`,
            paddingLeft: "0px",
            // bgcolor: theme.palette.neutral["005"],
          },
        },
        ...sx,
      }}
      onChange={handleChange}
      value={inputValue}
      {...rest}
    />
  );
};
